import { useEffect, useState } from "react";
import {
  Button,
  Box,
  Grid,
  Autocomplete,
  TextField,
  TextareaAutosize,
  Popper,
} from "@mui/material";

import { issueStatus } from "../../../services/constants";
import {
  updateExecutionIssues,
  upadateValidationIssues,
} from "../../../services/IssueManagementService";
import { updateRuleResearchIssueNew } from "../../../services/ResearchIssuesService";
import {
  capitalize,
  getFormattedDateInYYMMDD,
  getFormattedDateTime,
  getFormattedDateTimeWithT,
  getFormattedTimeInDobuleDigit,
  useToast,
  calculateDaysInQueue,
} from "../../../services/utils";
import { IconIssueClose } from "../../../common/utils/icons";
import useFetchIssueHistory from "../../../hooks/useFetchIssueHistory";
import Loader from "../Loader/Loader";
import { useAuth } from "../../../contexts/AuthContext";
import { useClassificationContext } from "../../../contexts/ClassificationContext";
import useFetchReasonsByClassification from "../../../hooks/useFetchReasonsByClassification";
interface IDashboardUserDetails {
  dashboardUserDetailsData?: any;
  setDashboardUserDetailsData: any;
  allUsersList: any;
  setIsLoading: any;
  setDetailViewDataRows: any;
  //issueFrom?: "comparison" | "validation" | "research";
  issueFrom?: "execution" | "validation" | "ruleResearch" | "genericResearch";
}
const DashboardUserDetails = ({
  dashboardUserDetailsData,
  setDashboardUserDetailsData,
  allUsersList,
  setIsLoading,
  setDetailViewDataRows,
  issueFrom = "execution",
}: IDashboardUserDetails) => {
  const { userInfo } = useAuth();
  const { showToast } = useToast();
  const [currentIssueId, setCurrentIssueId] = useState<
    number | null | undefined
  >(null);

  const [formData, setFormData] = useState<any>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Classifications from global context (fetched once for the whole app)
  const { allClassifications } = useClassificationContext();

  const activeClassifications = (allClassifications || []).filter(
    (c: any) => c?.is_active
  );

  // Fetch reasons only for the currently selected classification (optimized)
  const [fetchedReasons] = useFetchReasonsByClassification(
    formData?.issue_classification_id
  );

  const filteredReasons = fetchedReasons.filter((r: any) => r.is_active);
  const [issueHistoryList, setIssueHistoryList] = useState<any>([]);
  const [isUserDetailLoading, setIsUserDetailLoading] = useState(false);
  const [fetchedIssueHistory] = useFetchIssueHistory({
    currentIssueId,
    setIsLoading: setIsUserDetailLoading,
    issueFrom,
  });
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState({
    exceedCount: false,
  });
  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);

    setHasChanges(formChanged);
  }, [formData, initialFormData]);
  useEffect(() => {
    setIssueHistoryList(fetchedIssueHistory);
  }, [fetchedIssueHistory]);

  useEffect(() => {
    if (dashboardUserDetailsData) {
      setCurrentIssueId(dashboardUserDetailsData?.incidentId);
    }
  }, [dashboardUserDetailsData]);

  useEffect(() => {
    if (dashboardUserDetailsData) {
      console.log("dashboardUserDetailsData", dashboardUserDetailsData);
      const data = {
        incidentId: dashboardUserDetailsData?.incidentId,
        comment: "",
        status: dashboardUserDetailsData?.current_status,
        assigned_user: dashboardUserDetailsData?.assigned_user,
        assigned_by: userInfo?.username,
        issueId: dashboardUserDetailsData?.incidentId,
        issue_classification_id:
          dashboardUserDetailsData?.issue_classification_id || null,
        issue_reason_id: dashboardUserDetailsData?.issue_reason_id || null,
      };
      console.log("data", data);
      setFormData(data);
      setInitialFormData(data);
    }
  }, [dashboardUserDetailsData]);

  const handleChangeComment = (event: any) => {
    const newComment = event.target.value;
    const limitedComment =
      newComment.length <= 500 ? newComment : newComment.slice(0, 500);
    setCharCount(limitedComment.length);
    setFormData((prev: any) => ({
      ...prev,
      comment: limitedComment,
    }));
    setErrors((prev: any) => ({
      ...prev,
      exceedCount: limitedComment.length === 500,
    }));
  };

  const handleChangeStatus = (value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      status: value,
    }));
  };

  const handleClose = () => {
    setDashboardUserDetailsData((prev: any) => ({
      ...prev,
      status: false,
      issueAnalysis: [],
    }));
    setFormData({});
    setCurrentIssueId(null);
    setDashboardUserDetailsData({
      panelType:
        dashboardUserDetailsData.panelType === "incidentDetail"
          ? "incidentDetail"
          : "incidentResource",
    });
    setErrors((prev: any) => ({
      ...prev,
      exceedCount: false,
    }));
    setCharCount(0);
    setIssueHistoryList([]);
  };

  const handleSaveIssue = () => {
    const issueId = dashboardUserDetailsData?.incidentId;

    const payload: any = {
      assigned_user: formData?.assigned_user,
      assigned_by: formData?.assigned_by,
      comment: formData?.comment,
      status: formData?.status,
      issue_classification_id: formData?.issue_classification_id,
      issue_reason_id: formData?.issue_reason_id,
    };
    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(
        ([_key, value]) => value !== null && value !== ""
      )
    );

    if (issueFrom === "execution") {
      updateExecutionIssues(issueId, cleanedPayload)
        .then((response: any) => {
          if (response) {
            showToast("Issue updated successfully!", "success");
            setDetailViewDataRows((prevState: any) => {
              return prevState.map((item: any) => {
                if (item.rowId === dashboardUserDetailsData?.incidentId) {
                  return {
                    ...item,
                    current_status: formData?.status,
                    assigned_user: formData?.assigned_user,
                    comment: formData?.comment,
                    issue_classification_id: formData?.issue_classification_id,
                    issue_reason_id: formData?.issue_reason_id,
                  };
                }
                return item;
              });
            });
            const currentDate = new Date();
            const updatedData = {
              ...payload,
              create_date: getFormattedDateTimeWithT(currentDate),
              issue_id: currentIssueId,
            };
            issueHistoryList.unshift(updatedData);
            setCurrentIssueId(null);
            setDashboardUserDetailsData({
              panelType: "incidentDetail",
            });
          }
        })
        .catch((error) => {
          showToast(`Cannot update issue`, "error");
        });
    } else if (issueFrom === "validation") {
      upadateValidationIssues(issueId, payload)
        .then((response: any) => {
          if (response) {
            showToast("Issue updated successfully!", "success");
            setDetailViewDataRows((prevState: any) => {
              return prevState.map((item: any) => {
                if (item.rowId === dashboardUserDetailsData?.incidentId) {
                  return {
                    ...item,
                    current_status: formData?.status,
                    assigned_user: formData?.assigned_user,
                    comment: formData?.comment,
                    issue_classification_id: formData?.issue_classification_id,
                    issue_reason_id: formData?.issue_reason_id,
                  };
                }
                return item;
              });
            });
            const currentDate = new Date();
            const updatedData = {
              ...payload,
              create_date: getFormattedDateTimeWithT(currentDate),
              issue_id: currentIssueId,
            };
            issueHistoryList.unshift(updatedData);
            setCurrentIssueId(null);
            setDashboardUserDetailsData({
              panelType: "incidentDetail",
            });
          }
        })
        .catch((error) => {
          showToast(`Cannot update issue`, "error");
        });
    } else if (issueFrom === "ruleResearch") {
      updateRuleResearchIssueNew(issueId, cleanedPayload)
        .then((response: any) => {
          if (response) {
            showToast("Rule Research issue updated successfully!", "success");
            // For research issues, we call the refetch function instead of updating state
            setDetailViewDataRows((prevState: any) => {
              return prevState.map((item: any) => {
                if (item.rowId === dashboardUserDetailsData?.incidentId) {
                  return {
                    ...item,
                    current_status: formData?.status,
                    assigned_user: formData?.assigned_user,
                    comment: formData?.comment,
                    issue_classification_id: formData?.issue_classification_id,
                    issue_reason_id: formData?.issue_reason_id,
                  };
                }
                return item;
              });
            });
            const currentDate = new Date();
            const updatedData = {
              ...payload,
              create_date: getFormattedDateTimeWithT(currentDate),
              issue_id: currentIssueId,
            };
            issueHistoryList.unshift(updatedData);
            setCurrentIssueId(null);
            setDashboardUserDetailsData({
              panelType: "incidentDetail",
            });
          }
        })
        .catch((error) => {
          showToast(`Cannot update rule research issue`, "error");
        });
    } else if (issueFrom === "genericResearch") {
      updateRuleResearchIssueNew(issueId, cleanedPayload)
        .then((response: any) => {
          if (response) {
            showToast("Research issue updated successfully!", "success");
            // For research issues, we call the refetch function instead of updating state
            setDetailViewDataRows((prevState: any) => {
              return prevState.map((item: any) => {
                if (item.rowId === dashboardUserDetailsData?.incidentId) {
                  return {
                    ...item,
                    current_status: formData?.status,
                    assigned_user: formData?.assigned_user,
                    comment: formData?.comment,
                    issue_classification_id: formData?.issue_classification_id,
                    issue_reason_id: formData?.issue_reason_id,
                  };
                }
                return item;
              });
            });
            const currentDate = new Date();
            const updatedData = {
              ...payload,
              create_date: getFormattedDateTimeWithT(currentDate),
              issue_id: currentIssueId,
            };
            issueHistoryList.unshift(updatedData);
            setCurrentIssueId(null);
            setDashboardUserDetailsData({
              panelType: "incidentDetail",
            });
          }
        })
        .catch((error) => {
          showToast(`Cannot update research issue`, "error");
        });
    }

    handleClose();
  };

  const issueDataLength =
    dashboardUserDetailsData?.issueAnalysis?.length > 0
      ? Object.keys(dashboardUserDetailsData?.issueAnalysis[1]?.data).length
      : 0;
  const prefixPanelmaxLength =
    dashboardUserDetailsData?.issueAnalysis?.length > 0 &&
    dashboardUserDetailsData?.issueAnalysis[0]?.prefix.some(
      (item: any) => item.length > 20
    );
  return (
    <>
      <Loader isLoading={isUserDetailLoading} />
      <Box
        className={`filter-sidebar medium-panel ${
          dashboardUserDetailsData.panelType !== "incidentDetail"
            ? "large-panel"
            : ""
        } ${
          prefixPanelmaxLength
            ? "two"
            : issueDataLength === 1
            ? "one"
            : issueDataLength === 2
            ? "two"
            : ""
        }
        ${dashboardUserDetailsData?.status ? "show" : "hide"}`}
      >
        <div className="filter-header">
          {`${
            dashboardUserDetailsData.panelType === "incidentDetail"
              ? `Incident id: ${dashboardUserDetailsData?.incidentId}`
              : "Issue Analysis"
          }`}

          <div className="close-filter" onClick={handleClose}>
            <IconIssueClose />
          </div>
        </div>
        <div className="filter-body">
          {dashboardUserDetailsData.panelType === "incidentDetail" ? (
            <Box className="text-box-card bg-white no-radius border-0">
              <div className="accordion-panel alternative">
                <Grid container rowSpacing={3} columnSpacing={3}>
                  {/* Display First Occurrence */}
                  {dashboardUserDetailsData?.first_occurrence && (
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                      <Box
                        sx={{
                          padding: "12px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                          marginBottom: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <strong>Age:</strong>{" "}
                            {calculateDaysInQueue(
                              dashboardUserDetailsData?.first_occurrence
                            )}
                          </Box>
                          <Box sx={{ fontSize: "0.875rem", color: "#666" }}>
                            <strong>First Occurrence:</strong>{" "}
                            {getFormattedDateTime(
                              dashboardUserDetailsData?.first_occurrence
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Autocomplete
                      sx={{
                        "& .MuiSelect-select": {
                          height: "36px",
                        },
                      }}
                      className={`form-control-autocomplete capitalize`}
                      options={Array.isArray(issueStatus) ? issueStatus : []}
                      getOptionLabel={(option) => option}
                      onChange={(event, value) => handleChangeStatus(value)}
                      value={capitalize(formData?.status) || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Update Status"
                          variant="outlined"
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option === value}
                      PopperComponent={(props) => (
                        <Popper
                          sx={{
                            "& .MuiAutocomplete-listbox": {
                              "& .MuiAutocomplete-option": {
                                "&.Mui-focused[aria-selected='true']": {
                                  backgroundColor: "rgba(0, 0, 0, 0.10)",
                                },
                                "&.Mui-focused": {
                                  backgroundColor: "rgba(0, 0, 0, 0.10)",
                                },
                              },
                            },
                          }}
                          {...props}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Autocomplete
                      sx={{
                        "& .MuiSelect-select": {
                          height: "36px",
                        },
                      }}
                      className={`form-control-autocomplete`}
                      options={Array.isArray(allUsersList) ? allUsersList : []}
                      getOptionLabel={(option) => option.username}
                      onChange={(event, value) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          assigned_user: value?.username,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to user"
                          variant="outlined"
                        />
                      )}
                      value={
                        (allUsersList?.length > 0 &&
                          allUsersList?.find(
                            (user: { username: any }) =>
                              user.username === formData?.assigned_user
                          )) ||
                        null
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.username === value.username
                      }
                      PopperComponent={(props) => (
                        <Popper
                          sx={{
                            "& .MuiAutocomplete-listbox": {
                              "& .MuiAutocomplete-option": {
                                "&.Mui-focused[aria-selected='true']": {
                                  backgroundColor: "rgba(0, 0, 0, 0.10)",
                                },
                                "&.Mui-focused": {
                                  backgroundColor: "rgba(0, 0, 0, 0.10)",
                                },
                              },
                            },
                          }}
                          {...props}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Autocomplete
                      sx={{ "& .MuiSelect-select": { height: "36px" } }}
                      className={`form-control-autocomplete`}
                      options={activeClassifications}
                      getOptionLabel={(option: any) =>
                        option?.issue_classification_name || ""
                      }
                      value={
                        activeClassifications.find(
                          (c: any) => c.id === formData?.issue_classification_id
                        ) || null
                      }
                      onChange={(_event, value) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          issue_classification_id: value?.id || null,
                          issue_reason_id: null,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Classification"
                          variant="outlined"
                        />
                      )}
                      isOptionEqualToValue={(option: any, value: any) =>
                        option.id === value.id
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      className={`form-control-autocomplete reason-autocomplete`}
                      options={filteredReasons}
                      getOptionLabel={(option: any) =>
                        option?.reason_name || ""
                      }
                      value={
                        filteredReasons.find(
                          (r: any) => r.id === formData?.issue_reason_id
                        ) || null
                      }
                      onChange={(_event, value) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          issue_reason_id: value?.id || null,
                        }));
                      }}
                      disabled={!formData?.issue_classification_id}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          className={`${
                            (props as any).className ?? ""
                          } reason-autocomplete-option`}
                          title={option.reason_name}
                        >
                          {option.reason_name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Reason"
                          variant="outlined"
                          placeholder={
                            !formData?.issue_classification_id
                              ? "Select Classification first"
                              : "Select Reason"
                          }
                        />
                      )}
                      isOptionEqualToValue={(option: any, value: any) =>
                        option.id === value.id
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="incident-box"
                  >
                    <Box className="comment-section">
                      <Box>
                        <h3>
                          Comments&nbsp;
                          <span
                            className={`normal ${
                              errors?.exceedCount ? "required m-0" : ""
                            }`}
                          >
                            ({charCount}/500)
                          </span>
                        </h3>
                      </Box>
                      <Box className="comment-box">
                        {/* <strong className="mb-1">Add a Comment</strong> */}
                        <Box className="mb-2">
                          <TextareaAutosize
                            className={`form-control-1 textarea`}
                            minRows={3}
                            maxRows={6}
                            aria-label="minimum height"
                            placeholder="Add a comment"
                            value={formData?.comment}
                            name={"comment"}
                            onChange={handleChangeComment}
                          />
                        </Box>
                        {/* <Box className="d-flex justify-content-end">
                          <Button
                            className="btn-orange btn-sm"
                            onClick={handleAddComment}
                          >
                            Add comment
                          </Button>
                        </Box> */}
                        {/* <ul style={{ marginTop: "24px" }}>
                          {incidentComments?.map(
                            (comment: any, commentIndex: any) => (
                              <li key={commentIndex}>
                                <Box>
                                  <strong>User Name: </strong>
                                  {comment.assigned_user}
                                </Box>
                                <Box>
                                  <strong>Comment: </strong> {comment.comment}
                                </Box>
                                <Box>
                                  <strong>Date and time: </strong>
                                  {getFormattedDateTime(comment.create_date)}
                                </Box>
                              </li>
                            )
                          )}
                        </ul> */}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box className="issue-trails">
                  <IssueHistoryList issueHistoryList={issueHistoryList} />
                </Box>
              </div>
            </Box>
          ) : dashboardUserDetailsData?.issueAnalysis &&
            dashboardUserDetailsData?.issueAnalysis[0]?.prefix.length === 0 ? (
            <Box className="incident-resources">No Data Available</Box>
          ) : (
            <Box
              className="incident-resources"
              sx={{ maxHeight: "50vh", overflowY: "auto" }}
            >
              <Grid container rowSpacing={3} columnSpacing={3}>
                {dashboardUserDetailsData?.issueAnalysis &&
                  dashboardUserDetailsData?.issueAnalysis[0]?.prefix.map(
                    (prefix: any) => {
                      const data =
                        dashboardUserDetailsData?.issueAnalysis[1]?.data[
                          prefix
                        ];

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={
                            prefix.length > 20
                              ? 12
                              : issueDataLength === 1
                              ? 12
                              : issueDataLength === 2
                              ? 12
                              : 6
                          }
                          lg={
                            prefix.length > 20
                              ? 12
                              : issueDataLength === 1
                              ? 12
                              : issueDataLength === 2
                              ? 6
                              : 4
                          }
                          key={prefix}
                        >
                          <Box className="incident-list">
                            <h3>
                              <Box className="word-break-all">{prefix}</Box>
                            </h3>
                            <table className="table1">
                              {data.map((item: any, index: any) => {
                                const key = Object.keys(item)[0];
                                const value = item[key];
                                return (
                                  <tr key={index}>
                                    <td>
                                      <Box className="word-break-all">
                                        {key}
                                      </Box>
                                    </td>
                                    <td>
                                      <Box className="word-break-all">
                                        {value ?? "N/A"}
                                      </Box>
                                    </td>
                                  </tr>
                                );
                              })}
                            </table>
                          </Box>
                        </Grid>
                      );
                    }
                  )}
              </Grid>
            </Box>
          )}
        </div>
        {dashboardUserDetailsData.panelType === "incidentDetail" ? (
          <div className="filter-footer">
            <Button
              variant="contained"
              color="secondary"
              className="btn-orange"
              onClick={handleSaveIssue}
              disabled={!hasChanges}
            >
              Save
            </Button>

            <button
              className="btn-orange btn-border cursor-pointer"
              onClick={handleClose}
              color="secondary"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </Box>
    </>
  );
};

export default DashboardUserDetails;

const IssueHistoryList = ({
  issueHistoryList,
}: {
  issueHistoryList: any[];
}) => {
  return (
    <ul>
      {issueHistoryList?.map((item: any, index: number) => {
        const formattedDate = getFormattedDateInYYMMDD(item?.create_date);
        const formattedTime = getFormattedTimeInDobuleDigit(item?.create_date);

        return (
          <li key={index}>
            <div className="list-box">
              <div className="date-time-col">
                <div className="time-col">
                  <strong> Date & Time : </strong>
                  {formattedDate} - {formattedTime}
                </div>
              </div>

              {item?.assigned_by && <strong>{`${item?.assigned_by} `}</strong>}
              {item?.assigned_by && item?.assigned_user && (
                <>
                  assigned to <strong>{item?.assigned_user}</strong> and{" "}
                </>
              )}
              {item?.status && (
                <>
                  changed the Status as{" "}
                  <strong className={`status ${item?.status.toLowerCase()}`}>
                    {item?.status}
                  </strong>
                </>
              )}
              {item?.comment && (
                <div className="comment word-break-all max-width100 break-word">
                  <strong>Comment: </strong>
                  {`${item.comment}`}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
